// What the wizard tells the server about a session that never produced a run.
//
// The corpus is read and parsed entirely client-side, so the server hears
// nothing until a file has already parsed. Everything before that point was
// invisible: on 2026-07-30 a visitor arrived from an ad, took a code, signed
// the agreement, then hit the upload step and left. We only learned because
// he emailed.
//
// PRIVACY. These are the only messages Aurora sends about a file the user has
// not agreed to hand over, so they carry the SHAPE of what happened and
// nothing else. The rules, enforced here and re-enforced server-side:
//
//   - The filename never leaves the browser. Exports are named after the
//     parties ("Complete Message Record - <name> and <name>.pdf"), so the
//     name IS identity. Only the extension is derived from it.
//   - No page text, no message content, no parser error MESSAGE (pdf.js
//     strings can quote the document). Only an error class name, and only
//     when it looks like a JS identifier.
//   - Type sniffing reads the first 16 BYTES to identify a container format.
//     It never reads further, and those bytes are never sent — only the
//     verdict ("zip", "png").
//
// `buildWizardEvent` is the single construction point so those rules live in
// one testable place rather than at a dozen call sites.

export type WizardEventKind =
  // --- import dead ends -------------------------------------------------
  // pdf.js couldn't open it at all (corrupt, or not a PDF).
  | "pdf-open-failed"
  // Opened, but nothing fingerprinted it — the manual-pick state.
  | "pdf-nothing-scored"
  // An adapter matched confidently, then threw. The one that matters most:
  // we know this export and can't read this variant of it.
  | "pdf-detected-unreadable"
  // The user chose a format by hand and that parse threw.
  | "pdf-format-rejected"
  | "csv-unreadable"
  // Something that is neither PDF nor CSV reached the drop zone — a zipped
  // folder, a Word document, a screenshot of the messages.
  | "unsupported-file-type"
  // --- silences that aren't errors --------------------------------------
  // The file dialog was opened repeatedly and no file ever arrived: the
  // signature of an export the OS picker greyed out.
  | "picker-no-file"
  // The tab went away mid-wizard. Says WHICH step they were on.
  | "left-at-step"
  // Parsed fine and produced almost nothing. The dangerous one: it doesn't
  // error, so the user carries a broken record forward.
  | "thin-parse"
  // --- detection quality -------------------------------------------------
  // Detection wasn't confident enough to route on its own.
  | "format-confirm-shown"
  // The user picked a DIFFERENT format than we guessed, and it worked. The
  // only signal we get that detection guessed wrong.
  | "format-corrected"
  // --- attribution --------------------------------------------------------
  // "Where did you hear about Aurora?" — the optional one-tap question on
  // the run waiting screen (components/RunState HowHeardCard).
  | "how-heard";

// The tapped answer. Slugs, not labels — the labels live with the card.
export type HowHeardSource =
  | "facebook"
  | "private-group"
  | "linkedin"
  | "web-search"
  | "ai-chat"
  | "talk-podcast"
  | "referral"
  | "other";

export interface WizardEventPayload {
  kind: WizardEventKind;
  ext: string | null;
  size_bytes: number | null;
  pages: number | null;
  detected_format: string | null;
  score: number | null;
  error_name: string | null;
  /** Container format from the first bytes ("zip", "png") — not the extension. */
  actual_type: string | null;
  /** How many times the file dialog was opened with nothing to show for it. */
  attempts: number | null;
  message_count: number | null;
  chosen_format: string | null;
  guess_format: string | null;
  /** Wizard step label, e.g. "upload" / "parties" / "cost". */
  step: string | null;
  /** how-heard only: the tapped source slug. */
  how_heard_source: HowHeardSource | null;
  /** how-heard only: the typed follow-up ("who referred you?"). The ONE
   *  user-authored free-text field in this payload — volunteered, capped,
   *  and whitespace-collapsed here so the server's 200-char bound can
   *  never 422 an event the UI accepted. */
  how_heard_detail: string | null;
}

// A JS error class name — "InvalidPDFException", "TypeError". Prose and
// filenames fail this by construction (spaces, dots, dashes).
const ERROR_NAME_RE = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;

/** Extension only, lowercased, letters and digits — never the stem. */
export function extensionOf(fileName: string): string | null {
  const dot = fileName.lastIndexOf(".");
  if (dot < 0 || dot === fileName.length - 1) return null;
  const ext = fileName.slice(dot + 1).toLowerCase();
  return /^[a-z0-9]{1,15}$/.test(ext) ? ext : null;
}

/**
 * The class name of a thrown value, when it is safe to send.
 *
 * Deliberately NOT `err.message`: pdf.js and our own parsers quote document
 * text in messages, which is exactly the content these events must not carry.
 */
export function errorNameOf(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const name = (err as { name?: unknown }).name;
  if (typeof name === "string") {
    // A name that fails the shape check is REJECTED outright rather than
    // falling through to the constructor: whatever was in it, the answer is
    // "don't send", and reporting `Object` instead would be noise dressed up
    // as a diagnosis.
    return ERROR_NAME_RE.test(name) ? name : null;
  }
  const ctor = (err as { constructor?: { name?: unknown } }).constructor;
  if (
    ctor &&
    typeof ctor.name === "string" &&
    ctor.name !== "Object" &&
    ERROR_NAME_RE.test(ctor.name)
  ) {
    return ctor.name;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Container sniffing
// ---------------------------------------------------------------------------

/**
 * Identify a file by its leading bytes.
 *
 * Extensions lie in both directions: a zipped folder saved as `.pdf` goes
 * down the PDF path and dies inside pdf.js with an unhelpful error, and a
 * real export renamed by a user parses fine. Sixteen bytes settle it.
 *
 * Returns a short slug, or null when nothing matched (most likely plain
 * text, which is what a CSV is).
 */
export async function sniffFileType(file: Blob): Promise<string | null> {
  try {
    const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
    return sniffBytes(head);
  } catch {
    return null;
  }
}

export function sniffBytes(b: Uint8Array): string | null {
  const at = (i: number, ...want: number[]) =>
    want.every((w, k) => b[i + k] === w);

  if (at(0, 0x25, 0x50, 0x44, 0x46)) return "pdf"; // %PDF
  // Every Office/iWork document and every "compress this folder" is a zip.
  if (
    at(0, 0x50, 0x4b) &&
    (at(2, 0x03, 0x04) || at(2, 0x05, 0x06) || at(2, 0x07, 0x08))
  ) {
    return "zip";
  }
  // Legacy Word/Excel/Outlook (.doc/.xls/.msg) — OLE compound file.
  if (at(0, 0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1)) return "ole";
  if (at(0, 0xff, 0xd8, 0xff)) return "jpeg";
  if (at(0, 0x89, 0x50, 0x4e, 0x47)) return "png";
  if (at(0, 0x47, 0x49, 0x46, 0x38)) return "gif";
  if (at(0, 0x52, 0x49, 0x46, 0x46) && at(8, 0x57, 0x45, 0x42, 0x50)) return "webp";
  if (at(4, 0x66, 0x74, 0x79, 0x70)) {
    // ISO-BMFF: the brand at bytes 8..11 separates a phone photo from a video.
    const brand = String.fromCharCode(b[8], b[9], b[10], b[11]).toLowerCase();
    if (brand.startsWith("hei") || brand.startsWith("mif")) return "heic";
    return "video";
  }
  if (at(0, 0x7b, 0x5c, 0x72, 0x74, 0x66)) return "rtf"; // {\rtf
  if (at(0, 0x1f, 0x8b)) return "gzip";
  if (at(0, 0x52, 0x61, 0x72, 0x21)) return "rar";
  if (at(0, 0x37, 0x7a, 0xbc, 0xaf)) return "7z";
  return null;
}

/**
 * What to tell the person, given what they actually handed over.
 *
 * The old message ("Aurora reads PDF or CSV message exports") is true and
 * useless: it names our constraint instead of their next move. Someone who
 * dropped the zip their app emailed them needs to be told to open it.
 * Returns null when we can't do better than the generic line.
 */
export function describeUnsupportedFile(actualType: string | null): string | null {
  switch (actualType) {
    case "zip":
    case "gzip":
    case "rar":
    case "7z":
      return "That looks like a zipped folder. Open it and upload the PDF or spreadsheet from inside it.";
    case "ole":
    case "rtf":
      return "That looks like a Word or Excel document. Aurora needs the PDF or CSV your messaging app exports — a document written by hand won't carry the timestamps it reads.";
    case "jpeg":
    case "png":
    case "gif":
    case "webp":
    case "heic":
      return "That looks like a photo or a screenshot. Aurora reads the export file your messaging app produces, not pictures of the messages.";
    case "video":
      return "That looks like a video. Aurora reads the export file your messaging app produces.";
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Sending
// ---------------------------------------------------------------------------

// One event, one row. Several call sites report from inside a setState
// updater, and React StrictMode invokes updaters twice in dev — a build that
// still points at a real server would post every such event twice and quietly
// double the count. A short window also absorbs an accidental double-call
// anywhere else; it is far too short to swallow a genuine retry.
const DEDUPE_WINDOW_MS = 3000;
const recentSends = new Map<string, number>();

export function wizardEventKey(payload: WizardEventPayload): string {
  return [
    payload.kind,
    payload.ext ?? "",
    payload.size_bytes ?? "",
    payload.pages ?? "",
    payload.detected_format ?? "",
    payload.error_name ?? "",
    payload.actual_type ?? "",
    payload.attempts ?? "",
    payload.step ?? "",
    payload.how_heard_source ?? "",
  ].join("|");
}

/** True the first time this exact event is seen, false for a rapid repeat. */
export function shouldSendWizardEvent(
  payload: WizardEventPayload,
  now: number = Date.now(),
): boolean {
  const key = wizardEventKey(payload);
  const last = recentSends.get(key);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return false;
  recentSends.set(key, now);
  if (recentSends.size > 32) {
    for (const [k, t] of recentSends) {
      if (now - t >= DEDUPE_WINDOW_MS) recentSends.delete(k);
    }
  }
  return true;
}

export function resetWizardEventDedupeForTests(): void {
  recentSends.clear();
}

export function buildWizardEvent(args: {
  kind: WizardEventKind;
  file?: File | null;
  fileName?: string | null;
  sizeBytes?: number | null;
  pages?: number | null;
  detectedFormat?: string | null;
  score?: number | null;
  error?: unknown;
  actualType?: string | null;
  attempts?: number | null;
  messageCount?: number | null;
  chosenFormat?: string | null;
  guessFormat?: string | null;
  step?: string | null;
}): WizardEventPayload {
  // Take the name only to slice an extension off it; it is never stored on
  // the payload, and nothing downstream can reach it.
  const name = args.file?.name ?? args.fileName ?? "";
  const size = args.file?.size ?? args.sizeBytes ?? null;
  const int = (v: number | null | undefined) =>
    typeof v === "number" && Number.isFinite(v) && v >= 0 ? Math.floor(v) : null;
  return {
    kind: args.kind,
    ext: name ? extensionOf(name) : null,
    size_bytes: int(size),
    pages: int(args.pages),
    detected_format: args.detectedFormat ?? null,
    // Clamped: the server accepts 0..1 and would 422 an out-of-range score,
    // which would silently cost us the whole event.
    score:
      typeof args.score === "number" && Number.isFinite(args.score)
        ? Math.min(1, Math.max(0, args.score))
        : null,
    error_name: args.error === undefined ? null : errorNameOf(args.error),
    actual_type: args.actualType ?? null,
    attempts: int(args.attempts),
    message_count: int(args.messageCount),
    chosen_format: args.chosenFormat ?? null,
    guess_format: args.guessFormat ?? null,
    step: args.step ?? null,
    how_heard_source: null,
    how_heard_detail: null,
  };
}

// Mirrors the server's `_prose` bound: whitespace collapsed, control
// characters out, 200 chars. Clamping here means the server can never 422
// an answer the UI accepted — the same contract `score` holds above.
const HOW_HEARD_DETAIL_MAX = 200;

export function buildHowHeardEvent(
  source: HowHeardSource,
  detail: string | null,
): WizardEventPayload {
  const cleaned = (detail ?? "")
    // Control characters become word boundaries, then whitespace
    // collapses - the same order the server applies, so the two can
    // never disagree about an answer.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .join(" ")
    .slice(0, HOW_HEARD_DETAIL_MAX)
    .trim();
  return {
    ...buildWizardEvent({ kind: "how-heard" }),
    how_heard_source: source,
    how_heard_detail: cleaned || null,
  };
}

// ---------------------------------------------------------------------------
// Thin-parse heuristic
// ---------------------------------------------------------------------------

/**
 * Did this parse produce suspiciously little for the size of the file?
 *
 * A parse that returns nothing is a failure the user can see. A parse that
 * returns eleven messages out of a 4 MB, 300-page export is a failure nobody
 * sees: the wizard goes green, the quote comes back small, and the analysis
 * runs on a fraction of the record. That is worse than an error, so it gets
 * its own event.
 *
 * Thresholds are deliberately conservative — a genuinely short record (a
 * two-week exchange) must not trip this.
 */
export function isThinParse(args: {
  messageCount: number;
  pages?: number | null;
  sizeBytes?: number | null;
}): boolean {
  const { messageCount } = args;
  const pages = args.pages ?? 0;
  const bytes = args.sizeBytes ?? 0;
  if (messageCount === 0) return true;
  // ~20 pages of a message record holds far more than 10 messages in every
  // export format we support; likewise ~500 KB of text.
  const substantial = pages >= 20 || bytes >= 500_000;
  return substantial && messageCount < 10;
}
